import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ProblemCard } from "@/components/ProblemCard";
import { ClientLogosWithProfiles } from "@/components/ClientLogosWithProfiles";
import { StickyContactBar } from "@/components/StickyContactBar";
import { LeadCaptureForm } from "@/components/LeadCaptureForm";
import { BeforeAfterComparison } from "@/components/BeforeAfterComparison";
import { TestimonialsSection } from "@/components/TestimonialsSection";
import { FAQSection } from "@/components/FAQSection";
import { BrochureSection } from "@/components/BrochureSection";
import { AtAGlanceBox } from "@/components/AtAGlanceBox";
import { ValueDelivered } from "@/components/ValueDelivered";
import OdooBooking from "@/components/OdooBooking";
import { SEOHead } from "@/components/SEOHead";
import ConsultationBooking from "@/components/ConsultationBooking";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getVertical, getVerticals, getProblemsByVertical, getClientsByVertical, getDashboardSettings, getResources } from "@/lib/api";
import { getIcon } from "@/lib/icons";
import {
  ArrowLeft, AlertTriangle, ArrowLeftRight, Package, Quote, HelpCircle,
  FileText, Play, X, Video, Building2, CheckCircle2, Info, Calculator,
  Calendar, ChevronRight, Home, ShieldCheck, Clock, IndianRupee, ChevronDown,
  ArrowRight, Lightbulb, TrendingUp, BarChart3, Download, FolderOpen,
  Users, Check, Minus, GitBranch, Flag, CircleDot
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

const getYouTubeVideoId = (url) => {
  if (!url) return '';
  const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : '';
};

function ComparisonBadge({ value, isAbsolin }) {
  if (value === "yes") {
    return (
      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${isAbsolin ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-500'}`}>
        <Check className="w-4 h-4" />
      </span>
    );
  }
  if (value === "partial") {
    return (
      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${isAbsolin ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-400'}`}>
        <CircleDot className="w-4 h-4" />
      </span>
    );
  }
  return (
    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-50 text-slate-300">
      <Minus className="w-4 h-4" />
    </span>
  );
}

export default function VerticalDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [vertical, setVertical] = useState(null);
  const [problems, setProblems] = useState([]);
  const [clients, setClients] = useState([]);
  const [allVerticals, setAllVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [showMethodology, setShowMethodology] = useState(false);
  const [vertResources, setVertResources] = useState([]);
  const [showResourcesModal, setShowResourcesModal] = useState(false);
  const [seoOverride, setSeoOverride] = useState(null); // { title, description, focus_keyword }

  const headerRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [verticalRes, problemsRes, clientsRes, settingsRes, allVerticalsRes] = await Promise.all([
          getVertical(id),
          getProblemsByVertical(id),
          getClientsByVertical(id),
          getDashboardSettings().catch(() => ({ data: {} })),
          getVerticals().catch(() => ({ data: [] }))
        ]);
        setVertical(verticalRes.data);
        setProblems(problemsRes.data);
        setClients(clientsRes.data);
        setAllVerticals(allVerticalsRes.data || []);
        // Load resources for this vertical
        getResources({ vertical_id: id }).then(r => setVertResources(r.data || [])).catch(() => {});
        // Load SEO override (admin AI-generated meta) — silently ignore if missing
        try {
          const seoRes = await import("@/lib/api").then(m => m.default.get(`/seo/entity-meta/vertical/${id}`));
          if (seoRes?.data?.title || seoRes?.data?.description) setSeoOverride(seoRes.data);
        } catch (_) { /* no override */ }
      } catch (error) {
        toast.error("Failed to load industry details");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // Scroll to top when navigating between verticals
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        const headerBottom = headerRef.current.getBoundingClientRect().bottom;
        setIsHeaderSticky(headerBottom < 80);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const relatedVerticals = allVerticals.filter(v => v.id !== id).slice(0, 6);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-20 md:pt-24">
          <div className="max-w-5xl mx-auto px-4 py-16">
            <div className="animate-pulse space-y-6">
              <div className="h-4 w-48 bg-slate-200 rounded" />
              <div className="h-10 w-96 bg-slate-200 rounded" />
              <div className="h-4 w-full bg-slate-200 rounded" />
              <div className="h-4 w-3/4 bg-slate-200 rounded" />
              <div className="mt-12 space-y-8">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex gap-6">
                    <div className="w-12 h-12 bg-slate-200 rounded" />
                    <div className="w-20 h-24 bg-slate-200 rounded-xl" />
                    <div className="flex-1 space-y-3">
                      <div className="h-6 w-64 bg-slate-200 rounded" />
                      <div className="h-4 w-full bg-slate-200 rounded" />
                      <div className="h-4 w-3/4 bg-slate-200 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!vertical) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-20 md:pt-24 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <AlertTriangle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('common.industryNotFound')}</h2>
            <p className="text-slate-600 mb-6">{t('common.industryNotFoundDesc')}</p>
            <Link to="/verticals">
              <Button className="bg-[#0066CC] hover:bg-[#0052A3] rounded-full">
                {t('common.backToIndustries')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const hasDeliverables = vertical?.deliverables && vertical.deliverables.length > 0;

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={seoOverride?.title || (vertical ? `${vertical.name} - ERP & Digital Solutions` : "Industry Solutions")}
        description={seoOverride?.description || (vertical ? `${vertical.description?.slice(0, 155) || `Explore ${vertical.name} ERP solutions, challenges, and digital transformation strategies from Absolin.`}` : "Industry-specific ERP solutions")}
        keywords={seoOverride?.focus_keyword || (vertical ? `${vertical.name} ERP, ${vertical.name} digital transformation, ${vertical.name} software solutions, Odoo ERP ${vertical.name}, Focus X ERP` : "")}
        canonicalUrl={vertical ? `/vertical/${id}` : ""}
        ogType="article"
        breadcrumbs={vertical ? [
          { name: "Home", url: "/" },
          { name: "Industries", url: "/verticals" },
          { name: vertical.name }
        ] : null}
        faqItems={vertical?.faq?.map(f => ({ question: f.question || f.q, answer: f.answer || f.a })) || null}
        howToSteps={vertical?.implementation_timeline?.enabled && vertical.implementation_timeline.phases?.length > 0
          ? vertical.implementation_timeline.phases.map(ph => ({
              name: ph.title || ph.name || ph.phase || "",
              text: (ph.description || ph.summary || "") + (ph.duration ? ` (${ph.duration})` : ""),
            }))
          : null}
        howToName={vertical ? `${vertical.name} ERP Implementation Plan` : ""}
        howToDescription={vertical ? `Phased ERP implementation roadmap for ${vertical.name} businesses.` : ""}
      />
      <Navbar />

      <main className="pt-16 md:pt-20 pb-20">
        {/* ========= HERO SECTION ========= */}
        <section
          ref={headerRef}
          className="relative bg-gradient-to-br from-[#0066CC] via-[#0052A3] to-[#003366] overflow-hidden"
        >
          <div className="absolute inset-0 opacity-[0.04]">
            <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
          </div>

          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-white/60 mb-8" data-testid="breadcrumb-nav">
              <Link to="/" className="hover:text-white transition-colors flex items-center gap-1">
                <Home className="w-3.5 h-3.5" />
                <span>Home</span>
              </Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <Link to="/verticals" className="hover:text-white transition-colors">Industries</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-white font-medium">{vertical.name}</span>
            </nav>

            {/* Hero Content: Left Text + Right Visual */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-center">
              {/* Left: Text Content */}
              <div className="lg:col-span-3">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5">
                  The best {problems.length} <span className="text-blue-200">{vertical.name}</span> solutions
                </h1>
                <p className="text-base md:text-lg text-white/80 leading-relaxed mb-4 max-w-2xl">
                  {vertical.description}
                </p>
                <p className="text-sm text-white/60 leading-relaxed max-w-2xl">
                  Our curated solution library is designed to help you tackle every operational challenge in your industry.
                  Each solution includes step-by-step implementation, ROI projections, and real client outcomes.
                </p>

                {/* Hero Stats */}
                <div className="flex flex-wrap gap-4 mt-7">
                  {vertical.investment_range && (
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm">
                      <IndianRupee className="w-4 h-4 text-emerald-300" />
                      <span>{vertical.investment_range}</span>
                    </div>
                  )}
                  {vertical.total_duration && (
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm">
                      <Clock className="w-4 h-4 text-amber-300" />
                      <span>{vertical.total_duration}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm">
                    <ShieldCheck className="w-4 h-4 text-blue-300" />
                    <span>{problems.length} Solutions</span>
                  </div>
                </div>
              </div>

              {/* Right: Industry Visual */}
              <div className="lg:col-span-2 hidden lg:flex items-center justify-center">
                <div className="relative w-full max-w-sm">
                  {/* Industry icon card */}
                  <div className="w-full aspect-square rounded-3xl bg-white/10 backdrop-blur-sm border border-white/10 flex flex-col items-center justify-center p-8 shadow-2xl">
                    <div className="w-24 h-24 rounded-2xl bg-white/20 flex items-center justify-center mb-6">
                      {getIcon(vertical.icon, "h-14 w-14 text-white")}
                    </div>
                    <div className="text-center">
                      <p className="text-white font-semibold text-lg mb-1">{vertical.name}</p>
                      <p className="text-white/60 text-sm">{problems.length} problem statements & solutions</p>
                    </div>
                    {/* Floating badges */}
                    <div className="absolute -top-3 -right-3 w-16 h-16 rounded-2xl bg-emerald-500 flex items-center justify-center shadow-lg rotate-6">
                      <Calculator className="w-7 h-7 text-white" />
                    </div>
                    <div className="absolute -bottom-3 -left-3 w-14 h-14 rounded-2xl bg-amber-500 flex items-center justify-center shadow-lg -rotate-6">
                      <Lightbulb className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 mt-8">
              <Link to="/codn-calculator">
                <Button
                  className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-lg rounded-full px-6 w-full sm:w-auto justify-center"
                  data-testid="hero-codn-btn"
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  Cost of Doing Nothing
                </Button>
              </Link>
              <Link to="/assessment">
                <Button
                  className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-lg rounded-full px-6 w-full sm:w-auto justify-center"
                  data-testid="hero-assessment-btn"
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Digital Maturity Assessment
                </Button>
              </Link>
              <OdooBooking
                verticalId={id}
                verticalName={vertical.name}
                buttonText="Schedule a Demo"
                buttonClassName="bg-[#0066CC] hover:bg-[#0052A3] gap-2 rounded-full px-6 w-full sm:w-auto justify-center"
              />
              <Button
                variant="outline"
                onClick={() => setShowResourcesModal(true)}
                className="border-white/30 text-white hover:bg-white/10 gap-2 rounded-full px-6 w-full sm:w-auto justify-center"
                data-testid="hero-resources-btn"
              >
                <FolderOpen className="h-4 w-4" />
                Resources{vertResources.length > 0 ? ` (${vertResources.length})` : ''}
              </Button>
            </div>
          </div>
        </section>

        {/* ========= STICKY HEADER (on scroll) ========= */}
        <div
          className={`fixed top-16 md:top-20 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm transition-all duration-300 ${
            isHeaderSticky ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
          }`}
        >
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#0066CC]/10 flex items-center justify-center text-[#0066CC] flex-shrink-0">
                {getIcon(vertical.icon, "h-4 w-4")}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold text-slate-900 truncate text-sm">{vertical.name}</h2>
              </div>
              <div className="flex items-center gap-2">
                <Link to="/codn-calculator">
                  <Button
                    size="sm"
                    className="rounded-full text-xs hidden sm:flex bg-red-500 hover:bg-red-600 text-white"
                  >
                    <Calculator className="w-3 h-3 mr-1" />
                    CODN
                  </Button>
                </Link>
                <ConsultationBooking
                  verticalId={id}
                  verticalName={vertical.name}
                  buttonText={t('common.scheduleFreeConsultation')}
                  buttonSize="sm"
                  buttonClassName="bg-[#0066CC] hover:bg-[#0052A3] rounded-full text-xs gap-1"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ========= METHODOLOGY / TRUST BLOCK ========= */}
        <section className="border-b border-slate-100 bg-slate-50/50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setShowMethodology(!showMethodology)}
              className="w-full flex items-center justify-between py-4 text-left gap-3"
              data-testid="methodology-toggle"
            >
              <div className="flex items-start sm:items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-[#0066CC]/10 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-4 h-4 text-[#0066CC]" />
                </div>
                <span className="text-xs sm:text-sm text-slate-600 line-clamp-2 sm:line-clamp-none">
                  This solution library combines <strong className="text-slate-900">ABSOLIN's 11+ years</strong> of industry experience, real client implementations, and domain expertise.
                </span>
              </div>
              <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${showMethodology ? 'rotate-180' : ''}`} />
            </button>

            {showMethodology && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="pb-5 text-sm text-slate-600 space-y-3 pl-11"
              >
                <p>Our solutions are built from real-world implementations across hundreds of businesses:</p>
                <ol className="list-decimal pl-5 space-y-1.5">
                  <li>We identify recurring industry challenges through direct client consultations and on-ground surveys.</li>
                  <li>Each problem statement is validated against 50+ real implementations in the {vertical.name} sector.</li>
                  <li>Solutions include proven workflows, automation blueprints, and measurable KPI targets.</li>
                  <li>ROI projections are based on actual before/after data from our client deployments.</li>
                </ol>
              </motion.div>
            )}
          </div>
        </section>

        {/* ========= MAIN CONTENT AREA ========= */}
        <section className="py-10 md:py-14">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {hasDeliverables ? (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
                {/* Left Sidebar: Deliverables (Sticky) */}
                <div className="lg:col-span-5 xl:col-span-4 order-2 lg:order-1">
                  <div className="lg:sticky lg:top-36 space-y-5">
                    {/* Deliverables Card */}
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                      <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 rounded-xl bg-purple-500 flex items-center justify-center">
                          <Package className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900 text-sm">{t('vertical.whatYouGet')}</h3>
                          <p className="text-xs text-slate-500">{t('vertical.whatYouGetDesc', { industry: vertical.name })}</p>
                        </div>
                      </div>

                      <div className="space-y-2.5">
                        {vertical.deliverables.map((item, index) => (
                          <div key={item.name || `deliverable-${index}`} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                            <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-medium text-slate-900 text-sm">{item.title || item.name}</p>
                              {item.description && (
                                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{item.description}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {vertical.deliverables_summary && (
                        <div className="mt-4 p-3 rounded-lg bg-blue-50 border border-blue-100">
                          <div className="flex items-start gap-2">
                            <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-blue-800">{vertical.deliverables_summary}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Quick Contact Card */}
                    <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#0066CC] to-[#0052A3] p-5 text-white">
                      <h3 className="text-base font-semibold mb-2">{t('sidebar.needExpertGuidance')}</h3>
                      <p className="text-blue-100 text-sm mb-4">
                        {t('sidebar.consultantHelp')}
                      </p>
                      <ConsultationBooking
                        verticalId={id}
                        verticalName={vertical.name}
                        buttonText={t('common.scheduleFreeConsultation')}
                        buttonClassName="w-full bg-white text-[#0066CC] hover:bg-blue-50 font-semibold gap-2"
                      />
                    </div>
                  </div>
                </div>

                {/* Right: Main Content — Numbered Problem List */}
                <div className="lg:col-span-7 xl:col-span-8 order-1 lg:order-2">
                  {/* Section Header */}
                  <div className="mb-2">
                    <h2 className="text-2xl md:text-3xl font-bold text-slate-900 leading-tight">
                      {t('vertical.challengesWeSolve')}
                    </h2>
                    <p className="text-slate-500 mt-2 text-sm md:text-base leading-relaxed max-w-2xl">
                      {t('vertical.challengesDescription')}
                    </p>
                  </div>

                  {/* Numbered Problem List (Blinkist-style) */}
                  {problems.length > 0 ? (
                    <div className="mt-2" data-testid="problems-list">
                      {problems.map((problem, index) => (
                        <ProblemCard
                          key={problem.id}
                          problem={problem}
                          verticalName={vertical.name}
                          index={index}
                          verticalId={id}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-16 bg-slate-50 rounded-2xl mt-6">
                      <p className="text-slate-500 text-lg">{t('vertical.noProblemStatements')}</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* No-sidebar layout */
              <div>
                <div className="mb-2">
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-900">{t('vertical.challengesWeSolve')}</h2>
                  <p className="text-slate-500 mt-2 max-w-2xl">{t('vertical.challengesDescription')}</p>
                </div>
                {problems.length > 0 ? (
                  <div className="max-w-3xl mt-2" data-testid="problems-list">
                    {problems.map((problem, index) => (
                      <ProblemCard
                        key={problem.id}
                        problem={problem}
                        verticalName={vertical.name}
                        index={index}
                        verticalId={id}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-slate-50 rounded-2xl mt-6">
                    <p className="text-slate-500 text-lg">{t('vertical.noProblemStatements')}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* ========= AT A GLANCE + VALUE DELIVERED ========= */}
        <section className="py-10 md:py-14 bg-slate-50/80">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg md:text-xl font-bold text-slate-900">Business Impact & Overview</h2>
                <p className="text-sm text-slate-500">What we deliver for {vertical.name}</p>
              </div>
            </div>

            {/* At a Glance Row */}
            <AtAGlanceBox vertical={vertical} />

            {/* Value Delivered Grid */}
            {vertical?.value_delivered && Object.keys(vertical.value_delivered).length > 0 && (
              <div className="mt-4">
                <ValueDelivered values={vertical.value_delivered} />
              </div>
            )}
          </div>
        </section>

        {/* ========= SOLUTION VIDEOS ========= */}
        {vertical?.videos && vertical.videos.length > 0 && (
          <section className="py-14 md:py-18 bg-slate-900">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-3 mb-10">
                <div className="w-10 h-10 rounded-lg bg-red-600 flex items-center justify-center">
                  <Video className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white">
                    {t('vertical.solutionVideos')}
                  </h2>
                  <p className="text-slate-400 text-sm">{t('vertical.solutionVideosDesc', { industry: vertical.name })}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {vertical.videos.map((video, index) => {
                  const videoId = getYouTubeVideoId(video.youtube_url);
                  return (
                    <div
                      key={video.youtube_url || `video-${index}`}
                      className="group relative rounded-xl overflow-hidden bg-slate-800 cursor-pointer"
                      onClick={() => { setPlayingVideo(video); setVideoModalOpen(true); }}
                    >
                      <img
                        src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                        alt={video.title}
                        className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                        <div className="w-14 h-14 rounded-full bg-red-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Play className="h-6 w-6 text-white ml-1" />
                        </div>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                        <p className="text-white font-medium text-sm">{video.title}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* ========= BEFORE VS AFTER ========= */}
        {vertical?.before_after && vertical.before_after.length > 0 && (
          <section className="py-14 md:py-18 bg-gradient-to-br from-orange-50/50 to-white">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-3 mb-10">
                <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center">
                  <ArrowLeftRight className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
                    {t('vertical.beforeVsAfter')}
                  </h2>
                  <p className="text-slate-500 text-sm">{t('vertical.beforeVsAfterDesc', { industry: vertical.name })}</p>
                </div>
              </div>
              <BeforeAfterComparison items={vertical.before_after} />
            </div>
          </section>
        )}

        {/* ========= BROCHURES ========= */}
        {vertical?.brochures && vertical.brochures.length > 0 && (
          <section className="py-14 md:py-18 bg-slate-50">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-3 mb-10">
                <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-900">{t('vertical.downloadResources')}</h2>
                  <p className="text-slate-500 text-sm">{t('vertical.downloadResourcesDesc', { industry: vertical.name })}</p>
                </div>
              </div>
              <BrochureSection brochures={vertical.brochures} verticalName={vertical.name} />
            </div>
          </section>
        )}

        {/* ========= CLIENT LOGOS ========= */}
        {clients.length > 0 && (
          <section className="py-14 md:py-18 bg-white">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-10">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#0066CC] flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-white" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold text-slate-900">{t('problem.clientsBenefited')}</h2>
                </div>
                <p className="text-slate-500 text-sm">{t('problem.clientsHoverHint')}</p>
              </div>
              <ClientLogosWithProfiles clients={clients} />
            </div>
          </section>
        )}

        {/* ========= TESTIMONIALS ========= */}
        {vertical?.testimonials && vertical.testimonials.length > 0 && (
          <section className="py-14 md:py-18 bg-slate-50">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-3 mb-10">
                <div className="w-10 h-10 rounded-lg bg-[#0066CC] flex items-center justify-center">
                  <Quote className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-900">{t('vertical.whatOurClientsSay')}</h2>
                  <p className="text-slate-500 text-sm">{t('vertical.successStories', { industry: vertical.name })}</p>
                </div>
              </div>
              <TestimonialsSection testimonials={vertical.testimonials} />
            </div>
          </section>
        )}

        {/* ========= FAQ ========= */}
        {vertical?.faq && vertical.faq.length > 0 && (
          <section className="py-14 md:py-18">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-3 mb-10">
                <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center">
                  <HelpCircle className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-900">{t('vertical.faq')}</h2>
                  <p className="text-slate-500 text-sm">{t('vertical.faqDesc', { industry: vertical.name })}</p>
                </div>
              </div>
              <FAQSection items={vertical.faq} />
            </div>
          </section>
        )}

        {/* ========= COMPETITOR COMPARISON ========= */}
        {vertical?.competitor_comparison?.enabled && vertical.competitor_comparison.competitors?.length > 0 && vertical.competitor_comparison.features?.length > 0 && (
          <section className="py-14 md:py-18 bg-white" data-testid="competitor-comparison-section">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-3 mb-10">
                <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center">
                  <Users className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Why Choose ABSOLIN</h2>
                  <p className="text-slate-500 text-sm">See how we compare against alternatives for {vertical.name}</p>
                </div>
              </div>

              <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm">
                <table className="w-full text-sm" data-testid="competitor-matrix-table">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-5 py-4 font-semibold text-slate-700 min-w-[180px]">Feature</th>
                      <th className="text-center px-4 py-4 font-bold text-[#0066CC] min-w-[120px]">
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-xs uppercase tracking-wider">ABSOLIN</span>
                        </div>
                      </th>
                      {vertical.competitor_comparison.competitors.map((comp, ci) => (
                        <th key={comp.name || `competitor-${ci}`} className="text-center px-4 py-4 font-medium text-slate-600 min-w-[120px]">
                          <div className="flex flex-col items-center gap-1">
                            <span className="text-xs uppercase tracking-wider">{comp.name}</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {vertical.competitor_comparison.features.map((feature, fi) => (
                      <tr key={feature.name || `feature-${fi}`} className={`border-b border-slate-100 ${fi % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                        <td className="px-5 py-3.5 font-medium text-slate-800">{feature.name}</td>
                        <td className="px-4 py-3.5 text-center">
                          <ComparisonBadge value={feature.absolin} isAbsolin />
                        </td>
                        {vertical.competitor_comparison.competitors.map((comp, ci) => (
                          <td key={comp.name || `cell-${ci}`} className="px-4 py-3.5 text-center">
                            <ComparisonBadge value={feature.values?.[`comp_${ci}`] || "no"} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {/* ========= IMPLEMENTATION TIMELINE ========= */}
        {vertical?.implementation_timeline?.enabled && vertical.implementation_timeline.phases?.length > 0 && (
          <section className="py-14 md:py-18 bg-slate-50" data-testid="implementation-timeline-section">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-3 mb-10">
                <div className="w-10 h-10 rounded-lg bg-teal-600 flex items-center justify-center">
                  <GitBranch className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Implementation Timeline</h2>
                  <p className="text-slate-500 text-sm">Typical project phases for {vertical.name}</p>
                </div>
              </div>

              <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200 hidden md:block" />

                <div className="space-y-6">
                  {vertical.implementation_timeline.phases.map((phase, pi) => (
                    <div key={phase.name || `phase-${pi}`} className="relative flex gap-5" data-testid={`timeline-phase-${pi}`}>
                      {/* Phase number circle */}
                      <div className="relative z-10 flex-shrink-0">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#0066CC] to-[#0052A3] text-white flex items-center justify-center font-bold text-lg shadow-md">
                          {pi + 1}
                        </div>
                      </div>

                      {/* Phase content card */}
                      <div className="flex-1 bg-white rounded-xl border border-slate-200 shadow-sm p-5 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                          <h3 className="font-semibold text-slate-900 text-base">{phase.name}</h3>
                          {phase.duration && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-[#0066CC] rounded-full text-xs font-semibold">
                              <Clock className="w-3 h-3" />
                              {phase.duration}
                            </span>
                          )}
                        </div>

                        {/* Tasks */}
                        {phase.tasks && phase.tasks.length > 0 && (
                          <div className="mt-3 space-y-1.5">
                            {phase.tasks.map((task, ti) => (
                              <div key={task.title || `task-${ti}`} className="flex items-start gap-2 text-sm">
                                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                                <div>
                                  <span className="text-slate-700 font-medium">{task.title}</span>
                                  {task.description && (
                                    <span className="text-slate-500 ml-1">— {task.description}</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Milestones */}
                        {phase.milestones && phase.milestones.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {phase.milestones.map((ms, mi) => (
                              <span key={typeof ms === 'string' ? ms : `ms-${mi}`} className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium border border-emerald-200">
                                <Flag className="w-3 h-3" />
                                {ms}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ========= RELATED INDUSTRIES (Blinkist-style pill links) ========= */}
        {relatedVerticals.length > 0 && (
          <section className="py-14 md:py-18 border-t border-slate-100" data-testid="related-industries">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6">Related Industries</h2>
              <div className="flex flex-wrap gap-3">
                {relatedVerticals.map((v) => (
                  <Link
                    key={v.id}
                    to={`/vertical/${v.id}`}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-200 bg-white hover:border-[#0066CC] hover:text-[#0066CC] text-slate-700 text-sm font-medium transition-all hover:shadow-sm"
                    data-testid={`related-vertical-${v.id}`}
                  >
                    {getIcon(v.icon, "w-4 h-4")}
                    <span>{v.name}</span>
                    <ArrowRight className="w-3.5 h-3.5 opacity-0 -ml-1 group-hover:opacity-100 transition-opacity" />
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ========= CTA SECTION ========= */}
        <section className="py-14 md:py-18 bg-[#0066CC]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-4">
              {t('vertical.readyToTransform', { industry: vertical.name })}
            </h2>
            <p className="text-blue-100 mb-8 max-w-xl mx-auto text-sm sm:text-base">
              {t('vertical.transformDescription')}
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3">
              <ConsultationBooking
                verticalId={id}
                verticalName={vertical.name}
                buttonText={t('common.scheduleFreeConsultation')}
                buttonSize="lg"
                buttonClassName="bg-white text-[#0066CC] hover:bg-blue-50 rounded-full px-8 w-full sm:w-auto justify-center gap-2"
              />
              <Link to="/codn-calculator">
                <Button
                  size="lg"
                  className="bg-red-500 hover:bg-red-600 text-white rounded-full px-8 w-full sm:w-auto justify-center"
                >
                  <Calculator className="w-4 h-4 mr-2" />
                  Cost of Doing Nothing
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* ========= MODALS ========= */}

      {/* Video Modal */}
      <Dialog open={videoModalOpen} onOpenChange={setVideoModalOpen}>
        <DialogContent className="max-w-4xl p-0 bg-black overflow-hidden">
          <DialogHeader className="sr-only">
            <DialogTitle>{playingVideo?.title}</DialogTitle>
          </DialogHeader>
          <button
            onClick={() => setVideoModalOpen(false)}
            className="absolute top-2 right-2 z-50 w-8 h-8 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center"
          >
            <X className="h-5 w-5 text-white" />
          </button>
          {playingVideo && (
            <iframe
              src={`https://www.youtube.com/embed/${getYouTubeVideoId(playingVideo.youtube_url)}?autoplay=1`}
              className="w-full aspect-video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={playingVideo.title}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Lead Capture Form Dialog */}
      <LeadCaptureForm
        open={showLeadForm}
        onOpenChange={setShowLeadForm}
        verticalName={vertical?.name}
      />

      <StickyContactBar
        onOpenLeadForm={() => setShowLeadForm(true)}
        verticalId={id}
        verticalName={vertical?.name}
      />

      {/* Resources Modal */}
      <Dialog open={showResourcesModal} onOpenChange={setShowResourcesModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-[#0066CC]" />
              {vertical?.name} Resources
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2" data-testid="vertical-resources-list">
            {vertResources.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">No resources available for this vertical yet.</p>
            ) : (
              vertResources.map((r) => (
                <div key={r.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100 hover:border-blue-200 transition-colors">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                      <FileText className="w-4 h-4 text-[#0066CC]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{r.title}</p>
                      <p className="text-xs text-slate-400 truncate">{r.description || r.resource_type}</p>
                    </div>
                  </div>
                  {r.file_url && (
                    <a href={r.file_url} target="_blank" rel="noopener noreferrer"
                      className="p-2 bg-[#0066CC] text-white rounded-lg hover:bg-[#0052A3] transition-colors shrink-0 ml-2"
                      data-testid={`resource-download-${r.id}`}>
                      <Download className="w-4 h-4" />
                    </a>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
